import { skipToken, useSuspenseQuery } from '@apollo/client/react';
import { startTransition } from 'react';
import { LayoutGrid, SearchIcon } from 'lucide-react';
import { Link } from 'react-router';

import { AppsDocument, type IAppsQueryVariables } from 'src/graphql/actions/apps/apps.query';
import { withSuspense } from 'src/hoc';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Button } from '@/components/ui/button';
import Paginator from '@/components/ui/paginator';
import {
  ApplicationsContextProvider,
  ApplicationsToolbar,
  isUnsatisfiableQuery,
  toAppsQueryVariables,
  useApplications,
} from 'src/containers/Applications/Search';

import { AppCardWithProtections } from './AppCardWithProtections';

const PER_PAGE = 12;

const ApplicationsGrid = withSuspense(() => {
  const { query, page, setPage, activeFilterCount } = useApplications();

  const variables: Partial<IAppsQueryVariables> = { ...toAppsQueryVariables(query), page, perPage: PER_PAGE };

  const isUnsatisfiable = isUnsatisfiableQuery(query);

  const { data } = useSuspenseQuery(AppsDocument, isUnsatisfiable ? skipToken : { variables });
  const apps = data?.apps;

  const onPageChange = (nextPage: number) => startTransition(() => setPage(nextPage));

  const handleGoToFirstPage = () => onPageChange(1);

  const isFiltered = Boolean(query.search) || activeFilterCount > 0 || query.tab !== 'all';

  if (isUnsatisfiable || !apps || (!apps.nodesCount && isFiltered)) {
    return (
      <EmptyBlock
        icon={SearchIcon}
        title="No matches"
        description="No applications match your filters. Try widening them."
      />
    );
  }

  if (!apps.nodesCount) {
    return (
      <EmptyBlock
        icon={LayoutGrid}
        title="You haven't added any applications yet"
        description="Applications you own and applications you rely on both appear here"
        action={
          <Button asChild>
            <Link to="/applications/new">Create app</Link>
          </Button>
        }
      />
    );
  }

  if (!apps.nodes.length && page > 1) {
    return (
      <EmptyBlock
        icon={SearchIcon}
        title="No matches"
        description="There's nothing on this page."
        action={<Button onClick={handleGoToFirstPage}>Go to first page</Button>}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {apps.nodes.map((app) => (
          <AppCardWithProtections key={app.id} app={app} />
        ))}
      </div>
      <Paginator
        currentPage={page}
        totalPages={apps.pagesCount}
        onPageChange={onPageChange}
        showPreviousNext
        hideOnSinglePage
      />
    </div>
  );
});

const ApplicationsList = () => {
  const { query } = useApplications();

  return (
    <div className="space-y-4">
      <ApplicationsToolbar />
      <ApplicationsGrid key={JSON.stringify(query)} />
    </div>
  );
};

const ApplicationsListContainer = () => (
  <ApplicationsContextProvider>
    <ApplicationsList />
  </ApplicationsContextProvider>
);

export default ApplicationsListContainer;
